import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const CACHE_KEY = "atlas-rubro-marca-activa";

/**
 * Rubro de la **marca activa** (`brand_profiles.rubro`) del usuario.
 *
 * Existe aparte de `useBrandProfile` a propósito: ese hook trae el perfil
 * completo + créditos + la lista de marcas (3 queries y un montón de estado) y
 * cae al rubro de la EMPRESA cuando la marca no tiene el suyo. Acá hace falta
 * exactamente lo contrario — el rubro de la marca, sin fallback a la empresa —
 * para el gating de los modos de muebles
 * (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).
 *
 * "Marca activa" = el mismo criterio que el Sidebar y `useBrandProfile`:
 * `profiles.current_brand_id` → la marca `is_default` → la primera. Eso NO es
 * caer al rubro de la empresa: sigue siendo el rubro de una marca; es el
 * desempate de cuál marca cuando el usuario nunca eligió una.
 *
 * Cachea en localStorage (mismo patrón que `useUserGroup`) porque el consumidor
 * decide si una card se ve o no: sin cache, el modo aparecería un frame después
 * del primer paint en cada navegación. El fetch corre igual en cada montaje, así
 * que un cache viejo solo afecta ese primer frame. Se refresca con el evento
 * `atlas:brand-changed` (cambio de marca activa y ediciones de nombre/rubro).
 */
export interface RubroMarca {
  /** Rubro de la marca activa. `null` = desconocido todavía, o marca sin rubro. */
  rubro: string | null;
  /** `true` una vez que el fetch resolvió (aunque el rubro haya venido vacío). */
  loaded: boolean;
}

function readCache(): string | null {
  try {
    return localStorage.getItem(CACHE_KEY);
  } catch {
    return null;
  }
}

export function useRubroMarcaActiva(): RubroMarca {
  const [rubro, setRubro] = useState<string | null>(readCache);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchRubro = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company, current_brand_id")
          .eq("id", user.id)
          .single();
        if (cancelled) return;

        const idCompany = (profile as { id_company?: string | null } | null)
          ?.id_company;
        if (!idCompany) {
          // Sin empresa no hay marcas: rubro desconocido, pero el fetch terminó.
          setRubro(null);
          setLoaded(true);
          return;
        }

        // Mismo orden que `useBrandProfile.fetchBrands` para que "la marca
        // activa" signifique lo mismo en toda la app.
        const { data: brands } = await supabase
          .from("brand_profiles")
          .select("id, rubro, is_default")
          .eq("id_company", idCompany)
          .eq("is_draft", false)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: true });
        if (cancelled) return;

        const list =
          (brands as { id: string; rubro: string | null; is_default: boolean }[] | null) ??
          [];
        const currentId = (profile as { current_brand_id?: string | null } | null)
          ?.current_brand_id;
        const activa =
          list.find((b) => b.id === currentId) ??
          list.find((b) => b.is_default) ??
          list[0] ??
          null;

        const next = activa?.rubro ?? null;
        setRubro(next);
        setLoaded(true);
        try {
          if (next) localStorage.setItem(CACHE_KEY, next);
          else localStorage.removeItem(CACHE_KEY);
        } catch {
          /* noop */
        }
      } catch {
        /* noop — ante error mantenemos el valor cacheado */
      }
    };

    fetchRubro();
    // `atlas:brand-changed` lo emiten el switch de marca y las ediciones de
    // nombre/rubro — las dos cosas pueden cambiar este valor.
    const onBrandChanged = () => {
      fetchRubro();
    };
    window.addEventListener("atlas:brand-changed", onBrandChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("atlas:brand-changed", onBrandChanged);
    };
  }, []);

  return { rubro, loaded };
}
